import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { listReports, Report } from "../../lib/api";
import { ErrorNote, Loading } from "./ui";

/** Reports feed for admin roles. `GET /api/reports` is scope-filtered
 * on the backend, so a district_head already receives only reports
 * from godowns in their districts (across every QC agent working
 * there — the whole point). When a `godownIds` filter is passed we
 * further narrow to a single district's godowns, since a district_head
 * may own several districts but the page shows one at a time. */

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function VerdictBadge({ verdict }: { verdict: string }) {
  const v = (verdict || "").toLowerCase();
  const tone =
    v === "pass" || v === "accept"
      ? "bg-green-100 text-green-800"
      : v === "fail" || v === "reject"
        ? "bg-red-100 text-red-800"
        : "bg-gray-100 text-gray-600";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${tone}`}
    >
      {verdict || "—"}
    </span>
  );
}

export function ReportsTable({
  godownIds,
  emptyLabel = "No reports yet. They'll appear here as QC agents grade samples.",
}: {
  /** When set, only reports for these godowns are shown. */
  godownIds?: number[];
  emptyLabel?: string;
}) {
  const q = useQuery({
    queryKey: ["reports"],
    queryFn: listReports,
  });

  const rows: Report[] = useMemo(() => {
    const all = q.data?.reports ?? [];
    if (!godownIds) return all;
    const set = new Set(godownIds);
    return all.filter((r) => r.godown_id != null && set.has(r.godown_id));
  }, [q.data, godownIds]);

  if (q.isLoading) return <Loading label="Loading reports…" />;
  if (q.error) return <ErrorNote error={q.error} />;

  if (rows.length === 0) {
    return <div className="text-sm text-gray-500">{emptyLabel}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
            <th className="py-2 pr-4 font-medium">Date</th>
            <th className="py-2 pr-4 font-medium">Report</th>
            <th className="py-2 pr-4 font-medium">Godown</th>
            <th className="py-2 pr-4 font-medium">Grain</th>
            <th className="py-2 pr-4 font-medium">Verdict</th>
            <th className="py-2 pr-4 font-medium">QC agent</th>
            <th className="py-2 pr-4 font-medium text-right">Moisture</th>
            <th className="py-2 pr-4 font-medium text-right">Kernels</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="border-b border-gray-100 hover:bg-gray-50"
            >
              <td className="py-2 pr-4 whitespace-nowrap text-gray-600">
                {fmtDateTime(r.created_at)}
              </td>
              <td className="py-2 pr-4 font-mono text-xs text-gray-700">
                {r.report_id}
              </td>
              <td className="py-2 pr-4">{r.godown_name ?? "—"}</td>
              <td className="py-2 pr-4 capitalize">{r.grain_type ?? "—"}</td>
              <td className="py-2 pr-4">
                <VerdictBadge verdict={r.verdict} />
              </td>
              <td className="py-2 pr-4">{r.user_name ?? "—"}</td>
              <td className="py-2 pr-4 text-right tabular-nums">
                {r.moisture != null ? r.moisture.toFixed(1) : "—"}
              </td>
              <td className="py-2 pr-4 text-right tabular-nums">
                {r.kernels ?? 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
